import { airbrake } from '../entrypoints/airbrake'
import Rails from '@rails/ujs'
import { Controller } from '@hotwired/stimulus'
import DetectCardBrand from '../modules/detect_card_brand'

export default class extends Controller {
  static targets = ['entryForm', 'cardFunding', 'cardNumber', 'cardCvv' ,'cardName', 'cardAddress',
    'cardZip', 'cardExpMonth', 'cardExpYear', 'cardAuthorized', 'submit', 'loading',
    'fields', 'validationError', 'unexpectedError', 'hiddenForm', 'hiddenToken', 'hiddenReturnUrl',
    'hiddenNumber', 'hiddenCvv', 'hiddenName', 'hiddenAddress', 'hiddenZip', 'hiddenExp', 'iframe']

  connect() {
    this.handleIframeLoad()

    let brands = JSON.parse(this.entryFormTarget.getAttribute('data-brands'))
    this.detectCardBrand = new DetectCardBrand(brands)
  }

  submit(event) {
    event.preventDefault()  // We do not submit the CC info to our server
    this.storeCard()
  }

  storeCard() {
    this.hideErrors()
    this.enableSubmit(false)
    if (!this.valid()) {
      this.enableSubmit(true)
      return
    }
    this.submitCardDataToTrustCommerce()
  }

  // Request a new token from TrustCommerce and create a TrustCommerceToken record
  createTokenAndSubmit() {
    let params = [
      this.buildParam('funding', this.cardFundingTarget.value),
      this.buildParam('brand', this.brand())
    ].join('&')
    Rails.ajax({
      type: 'POST',
      url: this.hiddenFormTarget.getAttribute('data-create-token-path'),
      dataType: 'json',
      data: params,
      async: false,  // Wait for a response
      success: function(json) {
        this.fillHiddenForm(json)
        this.hiddenFormTarget.submit()
      }.bind(this),
      error: function(error) {
        this.showValidationErrors(true)
        this.enableSubmit(true)
      }.bind(this)
    })
  }

  // Place the token and the credit card info into the hidden form
  fillHiddenForm(token_data) {
    this.hiddenTokenTarget.value = token_data.token
    this.hiddenReturnUrlTarget.value = token_data.return_url

    this.hiddenNumberTarget.value = this.cardNumberTarget.value
    this.hiddenCvvTarget.value = this.cardCvvTarget.value
    this.hiddenNameTarget.value = this.cardNameTarget.value
    this.hiddenAddressTarget.value = this.cardAddressTarget.value
    this.hiddenZipTarget.value = this.cardZipTarget.value
    this.hiddenExpTarget.value = this.formattedExp()
  }

  submitCardDataToTrustCommerce() {
    this.showLoading(true)
    this.createTokenAndSubmit()
  }

  // When TrustCommerce responds, the iframe will be asked to redirect to the store path.
  // That action will create the TrustCommerceCard record and then the "load" event here
  // will advance to the payment confirmation page.
  handleIframeLoad() {
    this.iframeTarget.addEventListener('load', function() {
      let validationInfo = {}
      let crossOriginError = false
      try {
        let iframeDoc = this.iframeTarget.contentDocument || this.iframeTarget.contentWindow.document
        let element = iframeDoc.querySelector('.credit_card_iframe_validation_info')

        // Firefox triggers a 'load' event for the iframe during the initial page load and again
        // when TrustCommerceCardsController#store passes up its info. There will be no validation
        // element present on the initial page load. Do nothing then.
        if (element) {
          validationInfo = element.getAttribute('data-validation-info')
          validationInfo = JSON.parse(validationInfo)
        }
      } catch(err) {
        // If the payment processor returns a response to the iframe instead of redirecting
        // to our store action, we won't be able to read the response and there is nothing
        // we can do. Display a generic error message and don't send an Airbrake.
        if (err.message.indexOf('cross-origin') != -1) {
          crossOriginError = true
        } else {
          this.sendAirbrake(err)
        }
      }
      if (validationInfo && validationInfo['status'] == 'success') {
        window.location.href = validationInfo['url']
      } else {
        this.enableSubmit(true)
        if (crossOriginError) {
          this.showUnexpectedError(true)
        } else if (validationInfo['error_message']) {
          this.validationErrorTarget.innerText = validationInfo['error_message']
          this.showValidationErrors(true)
        } else {
          this.hideErrors()
        }
        this.showLoading(false)
      }
    }.bind(this))
  }

  showLoading(show) {
    if (show) {
      this.loadingTarget.classList.remove('d-none')
      this.fieldsTarget.classList.add('d-none')
    } else {
      this.loadingTarget.classList.add('d-none')
      this.fieldsTarget.classList.remove('d-none')
    }
  }

  showValidationErrors(show) {
    if (show) {
      this.validationErrorTarget.classList.remove('d-none')
      this.showLoading(false)
    } else {
      this.validationErrorTarget.classList.add('d-none')
    }
  }

  showUnexpectedError(show) {
    if (show) {
      this.unexpectedErrorTarget.classList.remove('d-none')
      this.showLoading(false)
    } else {
      this.unexpectedErrorTarget.classList.add('d-none')
    }
  }

  hideErrors() {
    this.showValidationErrors(false)
    this.showUnexpectedError(false)
  }

  enableSubmit(enable) {
    this.submitTarget.disabled = !enable
  }

  // Format the expiration as MMYY
  formattedExp() {
    let exp_month = this.cardExpMonthTarget.value
    let exp_year = this.cardExpYearTarget.value
    exp_year = exp_year.substr(2, 2)
    return '' + exp_month + exp_year
  }

  brand() {
    return this.detectCardBrand.detect(this.cardNumberTarget.value)
  }

  // Avoid short-circuiting here so we highlight all invalid fields
  valid() {
    let valid = true
    if (!this.cardNumberValid()) { valid = false }
    if (!this.cardNameValid()) { valid = false }
    if (!this.cardCvvValid()) { valid = false }
    if (!this.cardAddressValid()) { valid = false }
    if (!this.cardZipValid()) { valid = false }
    if (!this.cardExpMonthValid()) { valid = false }
    if (!this.cardExpYearValid()) { valid = false }
    if (!this.cardAuthorizedValid()) { valid = false }
    return valid
  }

  cardNumberValid() {
    let number = this.cardNumberTarget.value
    if (number.length > 0 && number.match(/^\d+$/) != null && this.brand() != undefined) {
      this.cardNumberTarget.classList.remove('is-invalid')
      return true
    } else {
      this.cardNumberTarget.classList.add('is-invalid')
      return false
    }
  }

  cardNameValid() {
    return this.fieldPresent(this.cardNameTarget)
  }

  cardAddressValid() {
    return this.fieldPresent(this.cardAddressTarget)
  }

  cardZipValid() {
    return this.fieldPresent(this.cardZipTarget)
  }

  cardExpMonthValid() {
    return this.fieldPresent(this.cardExpMonthTarget)
  }

  cardExpYearValid() {
    return this.fieldPresent(this.cardExpYearTarget)
  }

  cardCvvValid() {
    if (/^\d+$/.test(this.cardCvvTarget.value)) {
      this.cardCvvTarget.classList.remove('is-invalid')
      return true
    } else {
      this.cardCvvTarget.classList.add('is-invalid')
      return false
    }
  }

  fieldPresent(field) {
    if (field.value.length > 0) {
      field.classList.remove('is-invalid')
      return true
    } else {
      field.classList.add('is-invalid')
      return false
    }
  }

  cardAuthorizedValid() {
    if (!this.hasCardAuthorizedTarget) { return true }

    return this.fieldChecked(this.cardAuthorizedTarget)
  }

  fieldChecked(field) {
    if (field.checked) {
      field.classList.remove('is-invalid')
      return true
    } else {
      field.classList.add('is-invalid')
      return false
    }
  }

  sendAirbrake(err) {
    if (!airbrake) { return }

    airbrake.notify({
      error: err,
      params: { form_fields: this.formData() }
    })
  }

  formData() {
    let data = []
    let inputs = this.hiddenFormTarget.querySelectorAll('input')
    for (let i = 0; i < inputs.length; i++) {
      let input = inputs[i]
      let value = input.id == 'cc' || input.id == 'cvv' ? 'REMOVED' : input.value
      data.push({ name: input.id, value: value })
    }
    return data
  }

  buildParam(name, value) {
    return name + '=' + encodeURIComponent(value)
  }
}
